import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SignOut, ChartBar, CreditCard, User, Sparkle, Gift } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

export const UserMenu = () => {
  const { user, logout, isPro } = useAuth();
  const nav = useNavigate();

  if (!user) {
    return (
      <div className="flex items-center gap-2">
        <Link
          to="/login"
          data-testid="btn-header-login"
          className="text-sm text-white/85 hover:text-white px-3 py-2 rounded-lg border border-white/15 hover:border-white/25"
        >
          Sign in
        </Link>
        <Link
          to="/signup"
          data-testid="btn-header-signup"
          className="btn-neon inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm"
        >
          Sign up
        </Link>
      </div>
    );
  }

  const initials = (user.name || user.email || "?")
    .split(/\s+/)
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="user-menu-trigger"
          className="flex items-center gap-2 rounded-lg border border-white/15 hover:border-white/30 px-2 py-1.5 transition-colors"
        >
          {user.picture ? (
            <img
              src={user.picture}
              alt={user.name}
              className="w-7 h-7 rounded-full object-cover"
            />
          ) : (
            <div className="w-7 h-7 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 text-xs font-semibold grid place-items-center">
              {initials}
            </div>
          )}
          <span className="hidden md:inline text-sm text-white/90 max-w-[10rem] truncate">
            {user.name}
          </span>
          {isPro ? (
            <Badge
              data-testid="header-plan-badge"
              className="hidden md:inline bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/15 rounded-md text-[10px] uppercase tracking-[0.14em]"
            >
              Pro
            </Badge>
          ) : (
            <Badge
              data-testid="header-plan-badge"
              className="hidden md:inline bg-white/5 text-white/50 border border-white/15 hover:bg-white/5 rounded-md text-[10px] uppercase tracking-[0.14em]"
            >
              Free
            </Badge>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-64 z-[100] rounded-xl border border-white/10 bg-[#131924] text-slate-100 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.7),0_0_0_1px_rgba(16,185,129,0.08)] p-1.5"
      >
        <DropdownMenuLabel className="px-3 py-2.5 rounded-lg bg-white/[0.02] border border-white/5">
          <div className="flex items-center gap-2.5">
            {user.picture ? (
              <img
                src={user.picture}
                alt=""
                className="w-9 h-9 rounded-full object-cover border border-white/10"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-sm font-semibold grid place-items-center">
                {initials}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm text-white truncate">{user.name}</div>
              <div className="text-[11px] text-slate-400 truncate">{user.email}</div>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/5 my-1.5" />
        <DropdownMenuItem
          data-testid="menu-analytics"
          onClick={() => nav("/app/analytics")}
          className="cursor-pointer rounded-lg px-3 py-2 text-sm text-slate-200 focus:bg-white/5 focus:text-emerald-300 data-[highlighted]:bg-white/5 data-[highlighted]:text-emerald-300 transition-colors"
        >
          <ChartBar size={16} weight="bold" className="me-2.5 text-emerald-400 shrink-0" />
          <span>Analytics</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid="menu-referral"
          onClick={() => nav("/app/referral")}
          className="cursor-pointer rounded-lg px-3 py-2 text-sm text-slate-200 focus:bg-white/5 focus:text-emerald-300 data-[highlighted]:bg-white/5 data-[highlighted]:text-emerald-300 transition-colors"
        >
          <Gift size={16} weight="bold" className="me-2.5 text-emerald-400 shrink-0" />
          <span>Refer &amp; Earn</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid="menu-billing"
          onClick={() => nav("/app/billing")}
          className="cursor-pointer rounded-lg px-3 py-2 text-sm text-slate-200 focus:bg-white/5 focus:text-emerald-300 data-[highlighted]:bg-white/5 data-[highlighted]:text-emerald-300 transition-colors"
        >
          <CreditCard size={16} weight="bold" className="me-2.5 text-emerald-400 shrink-0" />
          <span className="flex-1">Billing</span>
          {isPro && (
            <Badge className="ms-2 bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/15 rounded-md text-[10px] uppercase tracking-[0.12em] px-1.5 py-0">
              Active
            </Badge>
          )}
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-white/5 my-1.5" />
        <DropdownMenuItem
          data-testid="menu-logout"
          onClick={logout}
          className="cursor-pointer rounded-lg px-3 py-2 text-sm text-slate-200 focus:bg-red-500/10 focus:text-red-300 data-[highlighted]:bg-red-500/10 data-[highlighted]:text-red-300 transition-colors"
        >
          <SignOut size={16} weight="bold" className="me-2.5 shrink-0" />
          <span>Sign out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
